import { createRootRouteWithContext, createRoute } from "@tanstack/react-router";
import type { RouterContext } from "@empite/app-kit";
import { Layout } from "./screens/layout";
import { Home } from "./screens/home";
import { SignIn } from "./screens/sign-in";
import type { SignInSearch } from "./screens/sign-in";
import { Register } from "./screens/register";
import { VerifyPhone } from "./screens/verify-phone";
import type { VerifyPhoneSearch } from "./screens/verify-phone";
import { Authorize } from "./screens/authorize";
import { DeviceApprovals } from "./screens/device-approvals";
import { Security } from "./screens/security";
import { Usage } from "./screens/usage";
import { Wallets } from "./screens/wallets";
import { NotFound } from "./screens/not-found";
import { WalletDetail } from "./screens/wallet-detail";
import type { WalletDetailSearch } from "./screens/wallet-detail";
import { parseAuthorizeSearch } from "./oauth";

/**
 * The route table.
 *
 * Every screen that reads the URL is handed its search parameters as a
 * PROP, parsed by the route's own validateSearch. Nothing downstream ever
 * touches a raw query string, so "is this parameter a string, and is it one
 * we recognise" has exactly one answer per route — which is what keeps a
 * hostile link from reaching a component as an unexpected type.
 */
function text(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

// `notFoundComponent` is a root-route OPTION, not a route: an unresolvable path
// still matches nothing, so it can never be linked to, preloaded or navigated
// into. It renders inside the root component's outlet, which is why Layout has
// to recognise the not-found state to give it the frame the design draws it in.
const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Layout,
  notFoundComponent: NotFound,
});

const homeRoute = createRoute({ getParentRoute: () => rootRoute, path: "/", component: Home });

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sign-in",
  validateSearch: (search: Record<string, unknown>): SignInSearch => ({
    next: text(search.next),
    client_id: text(search.client_id),
  }),
  component: () => <SignIn search={signInRoute.useSearch()} />,
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/register",
  component: Register,
});

const verifyPhoneRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verify-phone",
  validateSearch: (search: Record<string, unknown>): VerifyPhoneSearch => ({
    user_id: text(search.user_id),
    next: text(search.next),
  }),
  component: () => <VerifyPhone search={verifyPhoneRoute.useSearch()} />,
});

const authorizeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/authorize",
  validateSearch: parseAuthorizeSearch,
  component: () => <Authorize search={authorizeRoute.useSearch()} />,
});

const deviceApprovalsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/device-approvals",
  component: DeviceApprovals,
});

const securityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/security",
  component: Security,
});

const usageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/usage",
  component: Usage,
});

const walletsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/wallets",
  component: Wallets,
});

const walletDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/wallets/$applicationID",
  // The payment provider appends this on its way back. It is validated like
  // every other search parameter because it arrives from a third party: an
  // unrecognised value renders no banner rather than reaching a component as
  // an unexpected type.
  validateSearch: (search: Record<string, unknown>): WalletDetailSearch => ({
    topup: text(search.topup),
    subscribe: text(search.subscribe),
  }),
  component: () => (
    <WalletDetail
      applicationID={walletDetailRoute.useParams().applicationID}
      search={walletDetailRoute.useSearch()}
    />
  ),
});

export const routeTree = rootRoute.addChildren([
  homeRoute,
  signInRoute,
  registerRoute,
  verifyPhoneRoute,
  authorizeRoute,
  deviceApprovalsRoute,
  securityRoute,
  usageRoute,
  walletsRoute,
  walletDetailRoute,
]);
